import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calculator, User as UserIcon, MapPin, Package, Clock3, ArrowRight } from 'lucide-react';
import { UserProfile, FinancialConfig } from '../types';

interface Props {
  nicheName: string;
  serviceDescription: string;
  onComplete: (user: UserProfile, config: FinancialConfig, materialCost: number, timeSpent: number) => void;
}

export default function PreQuoteReview({ nicheName, serviceDescription, onComplete }: Props) {
  const [averagePrice, setAveragePrice] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  
  // Minimal form data
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [materialCost, setMaterialCost] = useState('');
  const [timeSpent, setTimeSpent] = useState('');

  useEffect(() => {
    // Simulate AI fetching average regional price
    const timer = setTimeout(() => {
      setAveragePrice(Math.floor(Math.random() * (350 - 120 + 1) + 120));
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create minimal user and config invisibly
    const newUser: UserProfile = {
      id: crypto.randomUUID(),
      ownerName: name,
      businessName: '',
      phone: '',
      niche: nicheName,
      profession: nicheName,
      actingType: 'Novo na área',
      city: city
    };

    const newConfig: FinancialConfig = {
      userId: newUser.id,
      proLaboreGoal: 2000,
      monthlyFixedCost: 500,
      weeklyWorkHours: 44,
      baseHourValue: 25
    };

    onComplete(newUser, newConfig, Number(materialCost), Number(timeSpent));
  };

  if (!averagePrice) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center p-6 text-white text-center">
        <div className="w-16 h-16 border-4 border-brand border-t-transparent rounded-full animate-spin mb-6" />
        <h2 className="text-xl font-bold mb-2">Analisando o mercado local...</h2>
        <p className="text-zinc-400 text-sm italic">"{serviceDescription}"</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 p-6 flex flex-col items-center py-12">
      <div className="w-full max-w-md">
        
        {!showForm ? (
          <div className="bg-white rounded-4xl p-6 shadow-2xl shadow-brand/20 animate-in fade-in zoom-in duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-rose-100 p-3 rounded-2xl">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <h2 className="text-zinc-900 font-black text-xl leading-tight">Olho no Preço da <br/>Concorrência!</h2>
              </div>
            </div>

            <p className="text-zinc-600 text-sm mb-6 leading-relaxed">
              Na sua região, o pessoal cobra em média <strong className="text-zinc-900 text-lg">R$ {averagePrice},00</strong>. 
              Se você cobrar menos que isso, pode acabar pagando para trabalhar.
            </p>

            <div className="bg-zinc-50 border border-zinc-100 p-4 rounded-xl mb-6">
              <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Serviço Identificado</p>
              <p className="text-zinc-900 font-medium italic">"{serviceDescription}"</p>
            </div>

            <button 
              onClick={() => setShowForm(true)}
              className="w-full bg-brand font-black text-white py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-brand/30 hover:bg-brand-dark transition-colors"
            >
              <Calculator className="h-5 w-5" />
              Ajustar Meu Preço Real
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-4xl p-6 shadow-2xl shadow-brand/20 animate-in slide-in-from-bottom-8 duration-300">
            <div className="mb-8">
              <h2 className="text-xl font-black text-zinc-900 mb-2">Para calcular seu lucro exato...</h2>
              <p className="text-sm text-zinc-500">Me diga rápido esses 4 dados vitais:</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input 
                  type="text" 
                  required
                  placeholder="Seu nome"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-12 pr-4 text-zinc-900 font-medium focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                />
              </div>

              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input 
                  type="text" 
                  required
                  placeholder="Sua cidade"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-12 pr-4 text-zinc-900 font-medium focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                />
              </div>

              <div className="relative">
                <Package className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input 
                  type="number" 
                  required
                  min="0"
                  placeholder="Custo do material (R$)"
                  value={materialCost}
                  onChange={e => setMaterialCost(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-12 pr-4 text-zinc-900 font-medium focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                />
              </div>

              <div className="relative">
                <Clock3 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input 
                  type="number" 
                  required
                  min="1"
                  placeholder="Tempo que vai gastar (min)"
                  value={timeSpent}
                  onChange={e => setTimeSpent(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-12 pr-4 text-zinc-900 font-medium focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                />
              </div>
            </div>

            <button 
              type="submit"
              className="w-full bg-zinc-900 font-black text-white py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-zinc-900/30 hover:bg-zinc-800 transition-colors"
            >
              Criar Orçamento Seguro
              <ArrowRight className="h-5 w-5" />
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
